/**
 * Question List Component
 * 
 * Displays the list of question uploads with tabs, search, and expandable rows
 * 
 * @component
 */

import React, { useState, useMemo } from "react";
import { Box, Typography, Tabs, Tab, TextField, InputAdornment, IconButton, Chip, Button as MuiButton, Table, TableHead, TableBody, TableRow, TableCell, TablePagination } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import UploadIcon from "@mui/icons-material/Upload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "../../ThemeContext";
import { formatUTCToLocalDateTime } from "../../utils/date-time-utils";

/**
 * QuestionList Component
 */
const QuestionList = ({
  uploads = [],
  standardQuestions = [],
  loading,
  onOpenUploadModal,
  selectedTab,
  onTabChange,
  searchQuery,
  onSearchChange,
  counts,
  onRefresh,
  customerDetails,
  selectedPath,
  onPathChange,
  onOpenFilterModal,
  onClearFilter,
  // Pagination props
  page = 0,
  rowsPerPage = 10,
  totalCount = 0,
  onPageChange,
  onRowsPerPageChange
}) => {
  const { themeMode, currentTheme } = useTheme();
  const [expandedRows, setExpandedRows] = useState(new Set());

  /**
   * Toggle row expansion
   */
  const toggleRowExpansion = (uploadId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(uploadId)) {
      newExpanded.delete(uploadId);
    } else {
      newExpanded.add(uploadId);
    }
    setExpandedRows(newExpanded);
  };

  /**
   * Filter standard questions based on search query (client-side for standard questions only)
   */
  const filteredStandardQuestions = useMemo(() => {
    if (!searchQuery.trim()) return standardQuestions;
    
    const query = searchQuery.toLowerCase();
    return standardQuestions.filter(question => 
      question.question?.toLowerCase().includes(query) ||
      question.expected_value?.toLowerCase().includes(query)
    );
  }, [standardQuestions, searchQuery]);

  /**
   * Format name for display
   */
  const formatName = (name) => {
    if (!name) return '';
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  /**
   * Clean expected value by removing curly braces
   */
  const cleanExpectedValue = (value) => {
    if (!value) return '';
    return value.replace(/\{/g, '').replace(/\}/g, '');
  };

  const isDark = themeMode === 'dark';
  const cardBackgroundColor = isDark ? '#1a1640' : '#ffffff';
  const borderColor = isDark ? '#302d4c' : '#e9e8ea';
  const textColor = currentTheme.text.primary;
  const secondaryTextColor = currentTheme.text.secondary;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 100px)', overflow: 'hidden' }}>
      {/* Fixed Header */}
      <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 600, 
              color: textColor,
              fontSize: '1.75rem',
              marginBottom: '0.5rem'
            }}
          >
            Question Management
          </Typography>
         
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Show Selected Path (if any) */}
          {selectedPath && (
            <Box 
              sx={{ 
                display: 'flex', 
                gap: 1, 
                alignItems: 'center',
                padding: '0.5rem 1rem',
                backgroundColor: isDark ? '#1a1640' : '#f7f7f7',
                borderRadius: '8px',
                border: `1px solid ${borderColor}`
              }}
            >
              <Typography sx={{ fontSize: '0.875rem', color: secondaryTextColor }}>
                Filter:
              </Typography>
              <Chip 
                label={formatName(selectedPath.branch)}
                size="small"
                sx={{ 
                  backgroundColor: isDark ? '#302d4c' : '#e9e8ea',
                  color: textColor,
                  fontWeight: 500
                }}
              />
              <Typography sx={{ fontSize: '0.875rem', color: secondaryTextColor }}>
                /
              </Typography>
              <Chip 
                label={formatName(selectedPath.usecase)}
                size="small"
                sx={{ 
                  backgroundColor: isDark ? '#302d4c' : '#e9e8ea',
                  color: textColor,
                  fontWeight: 500
                }}
              />
              <Typography sx={{ fontSize: '0.875rem', color: secondaryTextColor }}>
                /
              </Typography>
              <Chip 
                label={formatName(selectedPath.subusecase)}
                size="small"
                sx={{ 
                  backgroundColor: isDark ? '#302d4c' : '#e9e8ea',
                  color: textColor,
                  fontWeight: 500
                }}
              />
              <IconButton
                size="small"
                onClick={onClearFilter}
                sx={{
                  marginLeft: '0.25rem',
                  padding: '4px',
                  color: secondaryTextColor,
                  '&:hover': {
                    color: '#e63e2b',
                    backgroundColor: isDark ? '#302d4c' : '#f0f0f0'
                  }
                }}
              >
                <CloseIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Box>
          )}
          
          {/* Filter/Change Path button - Available on ALL tabs */}
          <MuiButton
            onClick={onOpenFilterModal}
            variant="outlined"
            startIcon={<TuneIcon />}
            sx={{
              borderColor: '#FF6B47',
              color: '#FF6B47',
              textTransform: 'none',
              padding: '0.5rem 1.5rem',
              borderRadius: '8px',
              fontWeight: 600,
              '&:hover': {
                borderColor: '#ff5530',
                backgroundColor: 'rgba(255, 107, 71, 0.08)'
              }
            }}
          >
            {selectedPath ? 'Change Filter' : 'Add Filter'}
          </MuiButton>
          
          {/* Upload Questions button - Available on ALL tabs */}
          <MuiButton
            onClick={onOpenUploadModal}
            variant="contained"
            startIcon={<UploadIcon />}
            sx={{
              backgroundColor: '#FF6B47',
              color: '#ffffff',
              fontWeight: 500,
              padding: '0.625rem 1.25rem',
              fontSize: '0.875rem',
              textTransform: 'none',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: '#FF5436'
              }
            }}
          >
            Upload Questions
          </MuiButton>
        </Box>
      </Box>

      {/* Fixed Tabs */}
      <Box sx={{ flexShrink: 0, borderBottom: `1px solid ${borderColor}`, mb: 2 }}>
        <Tabs 
          value={selectedTab} 
          onChange={(e, newValue) => onTabChange(newValue)}
          sx={{
            '& .MuiTab-root': {
              color: secondaryTextColor,
              textTransform: 'none',
              fontSize: '0.9375rem',
              fontWeight: 500,
              minHeight: '48px',
              '&.Mui-selected': {
                color: '#FF6B47'
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#FF6B47',
              height: '2px'
            }
          }}
        >
          <Tab 
            label="Question Sets" 
            value="custom"
          />
          <Tab 
            label="Standard Questions" 
            value="standard"
          />
          <Tab 
            label="Processing" 
            value="processing"
          />
        </Tabs>
      </Box>

      {/* Fixed Search Bar */}
      <Box sx={{ flexShrink: 0, display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
        <TextField
          size="small"
          fullWidth
          placeholder={selectedTab === 'standard' 
            ? 'Search by question or expected value...' 
            : 'Search by name, branch, usecase, uploaded by...'}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: secondaryTextColor, fontSize: '1.25rem' }} />
              </InputAdornment>
            ),
            sx: {
              backgroundColor: isDark ? '#1a1640' : '#ffffff',
              borderRadius: '8px',
              color: textColor,
              fontSize: '0.875rem',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: borderColor
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#FF6B47'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#FF6B47'
              },
              '& input::placeholder': {
                fontSize: '0.875rem',
                opacity: 0.7
              }
            }
          }}
        />
        {searchQuery && (
          <Chip
            label={`${selectedTab === 'standard' ? filteredStandardQuestions.length : totalCount} result${(selectedTab === 'standard' ? filteredStandardQuestions.length : totalCount) !== 1 ? 's' : ''}`}
            size="small"
            sx={{
              backgroundColor: '#FF6B47',
              color: '#ffffff',
              fontWeight: 500,
              fontSize: '0.75rem'
            }}
          />
        )}
      </Box>

      {/* Scrollable Content Area */}
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {selectedTab === 'standard' ? (
          // Standard Questions Table
          loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              minHeight: '400px'
            }}>
              <img
                src={themeMode === 'dark' ? '/icons/loader_white.gif' : '/icons/loader_black.gif'}
                alt="Loading..."
                style={{ width: '150px', height: '150px' }}
              />
            </Box>
          ) : !selectedPath ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
              <Typography sx={{ color: secondaryTextColor }}>
                Please select branch, usecase, and sub usecase to view standard questions
              </Typography>
            </Box>
          ) : filteredStandardQuestions.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
              <Typography sx={{ color: secondaryTextColor }}>
                {searchQuery.trim() 
                  ? `No standard questions found matching "${searchQuery}"`
                  : 'No standard questions found for this path'}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                backgroundColor: cardBackgroundColor,
                border: `1px solid ${borderColor}`,
                borderRadius: '12px',
                overflow: 'hidden',
                maxHeight: 'calc(100vh - 400px)'
              }}
            >
              <Box sx={{ overflow: 'auto', maxHeight: 'inherit' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          color: textColor, 
                          fontWeight: 600, 
                          width: '8%',
                          backgroundColor: isDark ? '#04002b' : '#f7f7f7',
                          borderBottom: `2px solid ${borderColor}`
                        }}
                      >
                        #
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          color: textColor, 
                          fontWeight: 600, 
                          width: '60%',
                          backgroundColor: isDark ? '#04002b' : '#f7f7f7',
                          borderBottom: `2px solid ${borderColor}`
                        }}
                      >
                        Question
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          color: textColor, 
                          fontWeight: 600, 
                          width: '32%',
                          backgroundColor: isDark ? '#04002b' : '#f7f7f7',
                          borderBottom: `2px solid ${borderColor}`
                        }}
                      >
                        Expected Value
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredStandardQuestions.map((question, index) => (
                      <TableRow
                        key={question._id}
                        sx={{
                          borderBottom: `1px solid ${borderColor}`,
                          '&:hover': {
                            backgroundColor: 'transparent !important'
                          },
                          '&.MuiTableRow-hover:hover': {
                            backgroundColor: 'transparent !important'
                          }
                        }}
                      >
                        <TableCell sx={{ color: textColor }}>{index + 1}</TableCell>
                        <TableCell sx={{ color: textColor }}>{question.question}</TableCell>
                        <TableCell sx={{ color: secondaryTextColor, fontSize: '0.875rem' }}>
                          {cleanExpectedValue(question.expected_value)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )
        ) : (
          // Custom/Processing Uploads List
          loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              minHeight: '400px'
            }}>
              <img
                src={themeMode === 'dark' ? '/icons/loader_white.gif' : '/icons/loader_black.gif'}
                alt="Loading..."
                style={{ width: '150px', height: '150px' }}
              />
            </Box>
          ) : uploads.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
            <Typography sx={{ color: secondaryTextColor }}>
              {searchQuery.trim() 
                ? `No results found for "${searchQuery}"` 
                : 'No question uploads found'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pb: 2 }}>
            {uploads.map((upload, i) => (
              <Box
                key={upload._id}
                sx={{
                  backgroundColor: cardBackgroundColor,
                  border: `1px solid ${borderColor}`,
                  borderRadius: '12px',
                  padding: '1rem 1.5rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#FF6B47',
                    transform: i === 0 ? 'translateY(.3rem)' : 'translateY(-.3rem)',
                    boxShadow: isDark 
                      ? '0 4px 12px rgba(0, 0, 0, 0.3)'
                      : '0 4px 12px rgba(0, 0, 0, 0.1)'
                  }
                }}
                onClick={() => toggleRowExpansion(upload._id)}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {/* Left side - Question set name and count */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography 
                      sx={{ 
                        color: textColor,
                        fontWeight: 500,
                        fontSize: '1rem'
                      }}
                    >
                      {upload.questionSetName || 'Unnamed Question Set'}
                    </Typography>
                    
                    {/* <Chip
                      label={`${upload.extractedQuestionsCount || 0} Questions`}
                      size="small"
                      sx={{
                        backgroundColor: isDark ? '#302d4c' : '#e9e8ea',
                        color: textColor,
                        fontWeight: 500,
                        fontSize: '0.75rem'
                      }}
                    /> */}
                  </Box>
                  
                  {/* Right side - Category and timestamp */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                      label={`${upload.branch} - ${upload.usecase} - ${upload.subusecase}`}
                      size="small"
                      sx={{
                        backgroundColor: isDark ? '#1a1640' : '#f0f0f0',
                        color: textColor,
                        border: `1px solid ${borderColor}`,
                        fontSize: '0.75rem'
                      }}
                    />
                    
                    <Typography 
                      sx={{ 
                        color: secondaryTextColor,
                        fontSize: '0.875rem',
                        minWidth: '180px',
                        textAlign: 'right'
                      }}
                    >
                      {formatUTCToLocalDateTime(upload.uploadedAt)}
                    </Typography>
                    
                    <IconButton size="small" sx={{ color: textColor }}>
                      {expandedRows.has(upload._id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                </Box>
                
                {/* Expanded content */}
                {expandedRows.has(upload._id) && (
                  <Box 
                    sx={{ 
                      marginTop: 2, 
                      paddingTop: 2, 
                      borderTop: `1px solid ${borderColor}`,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 4 }}>
                      {upload.fileName && (
                        <Box>
                          <Typography sx={{ color: secondaryTextColor, fontSize: '0.75rem' }}>
                            File Name
                          </Typography>
                          <Typography sx={{ color: textColor, fontSize: '0.875rem', fontWeight: 500 }}>
                            {upload.fileName}
                          </Typography>
                        </Box>
                      )}
                      
                      <Box>
                        <Typography sx={{ color: secondaryTextColor, fontSize: '0.75rem' }}>
                          Uploaded By
                        </Typography>
                        <Typography sx={{ color: textColor, fontSize: '0.875rem', fontWeight: 500 }}>
                          {upload.metadata?.uploadedBy || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
          )
        )}
      </Box>

      {/* Fixed Pagination at Bottom - Same style as StickyFlexTable */}
      {selectedTab !== 'standard' && !loading && (
        <Box sx={{ flexShrink: 0 }}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => onPageChange(newPage)}
            onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
            SelectProps={{
              sx: {
                backgroundColor: isDark ? '#1a1640' : '#ffffff',
                color: textColor,
                '& .MuiSvgIcon-root': {
                  color: textColor,
                },
              },
              MenuProps: {
                PaperProps: {
                  sx: {
                    backgroundColor: isDark ? '#1a1640' : '#ffffff',
                    color: textColor,
                  },
                },
              },
            }}
            sx={{
              color: textColor,
              backgroundColor: cardBackgroundColor,
              borderTop: `1px solid ${borderColor}`,
              '.MuiTablePagination-selectLabel, .MuiTablePagination-input, .MuiTablePagination-displayedRows': {
                color: textColor,
              },
              '.MuiTablePagination-actions': {
                color: textColor,
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default QuestionList;

